import React, { useState, useEffect } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { QrCode, ExternalLink, Users } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import PremiumLayout from '../components/layout/PremiumLayout';
import api from '../utils/api';
import LPEditorV4 from './LPEditorV4';
import QRModal from '../components/QRModal';
import AgentProfile from './AgentProfile';
import AgentAmbassadorsList from './AgentAmbassadorsList';

function DashboardHome() {
    const [stats, setStats] = useState({ leads: 0, activeLinks: 0, leadsToday: 0, activeLandingPages: 0 });
    const [ambassadorStats, setAmbassadorStats] = useState({ totalAmbassadors: 0, totalReferred: 0 });
    const [recentLeads, setRecentLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedLeadForDetail, setSelectedLeadForDetail] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [statsRes, leadsRes, ambRes] = await Promise.all([
                    api.get('/agent/dashboard/stats'),
                    api.get('/agent/leads'),
                    api.get('/agent/ambassadors/stats')
                ]);
                if (statsRes.data.success) {
                    setStats(statsRes.data.stats);
                }
                if (leadsRes.data.success) {
                    setRecentLeads((leadsRes.data.leads || []).slice(0, 5));
                }
                if (ambRes.data.success && ambRes.data.stats) {
                    setAmbassadorStats(ambRes.data.stats);
                }
            } catch (error) {
                console.error('Error fetching dashboard data:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    if (loading) return <div className="text-[var(--text-muted)]">Încărcare statistici...</div>;

    return (
        <div className="space-y-6">
            <div className="kpi-grid-4">
                <div className="kpi-card" style={{ animationDelay: '0.05s' }}>
                    <div className="kpi-top">
                        <span className="kpi-label">Total Lead-uri</span>
                        <div className="kpi-icon-wrap icon-blue">📊</div>
                    </div>
                    <div className="kpi-value c-blue">{stats.leads}</div>
                    <div className="kpi-trend trend-muted">aduse de tine</div>
                </div>
                <div className="kpi-card" style={{ animationDelay: '0.1s' }}>
                    <div className="kpi-top">
                        <span className="kpi-label">Lead-uri Astăzi</span>
                        <div className="kpi-icon-wrap icon-green">📈</div>
                    </div>
                    <div className="kpi-value c-green">{stats.leadsToday}</div>
                </div>
                <div className="kpi-card" style={{ animationDelay: '0.15s' }}>
                    <div className="kpi-top">
                        <span className="kpi-label">Link-uri Active</span>
                        <div className="kpi-icon-wrap icon-purple">🔗</div>
                    </div>
                    <div className="kpi-value c-purple">{stats.activeLinks}</div>
                </div>
                <div className="kpi-card" style={{ animationDelay: '0.2s' }}>
                    <div className="kpi-top">
                        <span className="kpi-label">Landing Pages Active</span>
                        <div className="kpi-icon-wrap icon-amber">📄</div>
                    </div>
                    <div className="kpi-value c-amber">{stats.activeLandingPages ?? 0}</div>
                </div>
                <div className="kpi-card" style={{ animationDelay: '0.22s' }}>
                    <div className="kpi-top">
                        <span className="kpi-label">Total Ambasadori</span>
                        <div className="kpi-icon-wrap icon-teal">🌟</div>
                    </div>
                    <div className="kpi-value c-teal">{ambassadorStats.totalAmbassadors ?? 0}</div>
                    <div className="kpi-trend trend-muted">Au adus {ambassadorStats.totalReferred ?? 0} clienți noi</div>
                </div>
            </div>

            <div className="section-header">
                <span className="section-title">Lead-uri Recente</span>
                <span className="section-line" />
                <Link to="/agent/leads" className="section-badge badge-platform" style={{ textDecoration: 'none' }}>
                    Vezi toate →
                </Link>
            </div>
            <div className="agent-leads-table-wrap overflow-x-auto w-full">
                <table className="agent-leads-table">
                    <thead>
                        <tr>
                            <th>Nume</th>
                            <th>Telefon</th>
                            <th>Data</th>
                            <th>Acțiuni</th>
                        </tr>
                    </thead>
                    <tbody>
                        {recentLeads.length === 0 ? (
                            <tr>
                                <td colSpan={4} className="leads-empty">Nu ai niciun lead încă.</td>
                            </tr>
                        ) : (
                            recentLeads.map((lead) => (
                                <tr key={lead.id}>
                                    <td>{lead.nume} {lead.prenume}</td>
                                    <td>{lead.telefon}</td>
                                    <td>{new Date(lead.dataCompletare).toLocaleDateString('ro-RO')}</td>
                                    <td>
                                        <button type="button" onClick={() => { setSelectedLeadForDetail(lead); setShowDetailModal(true); }} className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-slate-500/15 text-slate-400 hover:bg-slate-500/25 rounded-lg border border-slate-500/30" title="Detalii">👁 Detalii</button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
            {showDetailModal && selectedLeadForDetail && (
                <LeadDetailModal lead={selectedLeadForDetail} onClose={() => { setShowDetailModal(false); setSelectedLeadForDetail(null); }} />
            )}
        </div>
    );
}

function MyLeads() {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedLeadForDetail, setSelectedLeadForDetail] = useState(null);

    useEffect(() => {
        const fetchLeads = async () => {
            try {
                const response = await api.get('/agent/leads');
                if (response.data.success) {
                    setLeads(response.data.leads);
                }
            } catch (error) {
                console.error('Error fetching leads:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchLeads();
    }, []);

    if (loading) return <div className="text-[var(--text-muted)]">Încărcare lead-uri...</div>;

    return (
        <>
        <div className="agent-leads-table-wrap overflow-x-auto w-full">
            <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--border)' }}>
                <div className="card-title">Lead-urile Mele</div>
                <div className="card-sub">Toate lead-urile primite</div>
            </div>
            <table className="agent-leads-table">
                <thead>
                    <tr>
                        <th>Nume</th>
                        <th>Telefon</th>
                        <th>Campanie</th>
                        <th>Data</th>
                        <th>Acțiuni</th>
                    </tr>
                </thead>
                <tbody>
                    {leads.length === 0 ? (
                        <tr>
                            <td colSpan={5} className="leads-empty">Nu ai niciun lead încă.</td>
                        </tr>
                    ) : (
                        leads.map((lead) => (
                            <tr key={lead.id}>
                                <td>{lead.nume} {lead.prenume}</td>
                                <td>{lead.telefon}</td>
                                <td>
                                    <div className="flex flex-col gap-1">
                                        <span>{lead.landingPage?.titlu || '-'}</span>
                                        {lead.referredByCode && (
                                            <span className="inline-flex items-center w-fit px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 border border-emerald-500/40" title={lead.referredByName ? `Recomandat de ${lead.referredByName}` : `Cod: ${lead.referredByCode}`}>
                                                Recomandare: {lead.referredByName || lead.referredByCode || 'Ambasador'}
                                            </span>
                                        )}
                                    </div>
                                </td>
                                <td>{new Date(lead.dataCompletare).toLocaleDateString('ro-RO')}</td>
                                <td>
                                    <button
                                        type="button"
                                        onClick={() => { setSelectedLeadForDetail(lead); setShowDetailModal(true); }}
                                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-slate-500/15 text-slate-400 hover:bg-slate-500/25 border border-slate-500/30"
                                        title="Detalii lead"
                                    >
                                        👁 Detalii
                                    </button>
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
        {showDetailModal && selectedLeadForDetail && (
            <LeadDetailModal
                lead={selectedLeadForDetail}
                onClose={() => { setShowDetailModal(false); setSelectedLeadForDetail(null); }}
            />
        )}
        </>
    );
}

// Modal detaliu lead pentru agent (inclusiv câmpuri custom)
function LeadDetailModal({ lead, onClose }) {
    let customFieldsParsed = {};
    if (lead.customData) {
        try {
            customFieldsParsed = typeof lead.customData === 'string' ? JSON.parse(lead.customData) : lead.customData;
        } catch (_) {
            customFieldsParsed = {};
        }
    }
    const hasCustomFields = Object.keys(customFieldsParsed).length > 0;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]">
            <div className="bg-white dark:bg-[#1a1d27] rounded-xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-xl border border-gray-200 dark:border-white/10">
                <h3 className="text-lg font-bold mb-4 text-[var(--text)]">Detalii Lead</h3>
                <div className="space-y-3 text-sm">
                    <div><span className="font-semibold text-[var(--text-muted)]">Nume:</span> <span className="text-[var(--text)]">{lead.nume || '—'} {lead.prenume || ''}</span></div>
                    <div><span className="font-semibold text-[var(--text-muted)]">Email:</span> <span className="text-[var(--text)]">{lead.email || '—'}</span></div>
                    <div><span className="font-semibold text-[var(--text-muted)]">Telefon:</span> <span className="text-[var(--text)]">{lead.telefon || '—'}</span></div>
                    <div><span className="font-semibold text-[var(--text-muted)]">Campanie:</span> <span className="text-[var(--text)]">{lead.landingPage?.titlu || '—'}</span></div>
                    <div><span className="font-semibold text-[var(--text-muted)]">Data completare:</span> <span className="text-[var(--text)]">{lead.dataCompletare ? new Date(lead.dataCompletare).toLocaleString('ro-RO') : '—'}</span></div>
                </div>
                {hasCustomFields && (
                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-white/10">
                        <h4 className="text-sm font-bold mb-2 text-[var(--text)]">Câmpuri completate de client</h4>
                        <div className="space-y-2">
                            {Object.entries(customFieldsParsed).map(([key, value]) => (
                                <div key={key} className="flex gap-2">
                                    <span className="font-medium text-[var(--text-muted)] shrink-0">{key}:</span>
                                    <span className="text-[var(--text)] break-words">{value != null && value !== '' ? String(value) : '—'}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                <div className="mt-6 flex justify-end">
                    <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg bg-[var(--bar-bg)] text-[var(--text)] hover:bg-gray-300 dark:hover:bg-white/15">
                        Închide
                    </button>
                </div>
            </div>
        </div>
    );
}



// End of QRModal removal

// ... (keep logic)

// End of QRModal removal

function LandingPages() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [landingPages, setLandingPages] = useState([]);
    const [myLinks, setMyLinks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(null);
    const [message, setMessage] = useState(null);
    const [showQRModal, setShowQRModal] = useState(false);
    const [selectedQRLink, setSelectedQRLink] = useState(null);

    const fetchData = async () => {
        try {
            const [lpsRes, linksRes] = await Promise.all([
                api.get('/agent/landing-pages'),
                api.get('/agent/links')
            ]);

            if (lpsRes.data.success) {
                setLandingPages(lpsRes.data.landingPages);
            }
            if (linksRes.data.success) {
                // Enrich links with fullUrl for QR; leadsCount vine din backend
                const enrichedLinks = (linksRes.data.links || []).map(l => ({
                    ...l,
                    fullUrl: `${window.location.origin}/lp/${l.landingPage?.slug}?ref=${l.codUnic}`,
                    leadsCount: l.leadsCount ?? 0
                }));
                setMyLinks(enrichedLinks);
            }

        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleGenerateLink = async (lpId) => {
        setGenerating(lpId);
        setMessage(null);
        try {
            const response = await api.post('/agent/link', { landingPageId: lpId });
            if (response.data.success) {
                setMessage({ type: 'success', text: response.data.message });
                fetchData(); // Refresh all
            }
        } catch (error) {
            setMessage({ type: 'error', text: error.response?.data?.error || 'Eroare.' });
        } finally {
            setGenerating(null);
        }
    };

    const handleEditLp = (lp) => {
        navigate(`/agent/editor-lp/${lp.id}`);
    };

    const handleOpenQR = (link) => {
        setSelectedQRLink(link);
        setShowQRModal(true);
    };

    if (loading) return <div>Incarcare campanii...</div>;

    const opTemplates = landingPages.filter(lp => !lp.agentId && lp.activ);
    const myCustomLps = landingPages.filter(lp => lp.agentId === user.id);

    return (
        <div className="space-y-8">
            {message && (
                <div className={`p-4 rounded-md ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                    {message.text}
                </div>
            )}

            <div className="flex justify-between items-center bg-white dark:bg-slate-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700">
                <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">Campanii & Landing Pages</h2>
                    <p className="text-gray-500 dark:text-gray-400">Gestionează campaniile disponibile și link-urile tale.</p>
                </div>
                <button
                    onClick={() => navigate('/agent/editor-lp')}
                    className="bg-brand-blue text-white px-4 py-2 rounded-lg hover:bg-brand-blue-hover transition shadow-sm"
                >
                    + Creează LP Custom
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Left Column: OP Templates */}
                <div className="space-y-6">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white border-b border-gray-200 dark:border-slate-700 pb-2">Template-uri Disponibile (Manager de Agenți)</h3>
                    <div className="grid grid-cols-1 gap-4">
                        {opTemplates.length === 0 ? <p className="text-gray-500 dark:text-gray-400 italic">Niciun template disponibil.</p> :
                            opTemplates.map(lp => (
                                <div key={lp.id} className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-5 shadow-sm hover:shadow-md transition">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <h4 className="font-bold text-md text-brand-blue">{lp.titlu}</h4>
                                            <div
                                                className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-2 prose prose-sm dark:prose-invert"
                                                dangerouslySetInnerHTML={{ __html: lp.descriere || 'Fără descriere' }}
                                            />
                                        </div>
                                        <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">Activ</span>
                                    </div>
                                    <button
                                        onClick={() => handleGenerateLink(lp.id)}
                                        disabled={generating === lp.id}
                                        className="mt-4 w-full bg-white dark:bg-slate-700 border border-brand-blue dark:border-blue-400 text-brand-blue dark:text-blue-300 py-2 rounded hover:bg-brand-blue hover:text-white transition disabled:opacity-50 text-sm font-medium"
                                    >
                                        {generating === lp.id ? 'Se generează...' : 'Generează Link Rapid'}
                                    </button>
                                </div>
                            ))
                        }
                    </div>
                </div>

                {/* Right Column: Active Links & Custom LPs */}
                <div className="space-y-6">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white border-b border-gray-200 dark:border-slate-700 pb-2">Link-urile & Campaniile Mele</h3>

                    {/* My Custom LPs List */}
                    <div className="mb-6">
                        <h4 className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-3 uppercase tracking-wider">Campanii Custom Create</h4>
                        {myCustomLps.length === 0 ? <p className="text-sm text-gray-400 dark:text-gray-500 italic">Nu ai creat nicio campanie custom.</p> :
                            <div className="space-y-3">
                                {myCustomLps.map(lp => (
                                    <div key={lp.id} className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded p-3 flex justify-between items-center">
                                        <div>
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <p className="font-medium text-sm">{lp.titlu}</p>
                                                {lp.isTemplate && <span className="text-xs bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300 px-1.5 py-0.5 rounded">Template</span>}
                                                <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${lp.isDraft ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300' : 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'}`}>
                                                  {lp.isDraft ? 'Ciornă' : 'Publicat'}
                                                </span>
                                            </div>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">/lp/{lp.slug}</p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => handleEditLp(lp)}
                                                className="text-gray-600 dark:text-gray-300 hover:text-blue-600 text-xs px-2 py-1 bg-gray-50 dark:bg-slate-700 hover:bg-blue-50 dark:hover:bg-slate-600 rounded border border-gray-200 dark:border-slate-600"
                                            >
                                                Editează
                                            </button>

                                            {(lp.status === 'activ' || lp.activ) && lp.status !== 'respins' ? (
                                                <>
                                                    <button
                                                        onClick={() => handleOpenQR({
                                                            fullUrl: `${window.location.origin}/lp/${lp.slug}`,
                                                            agent: user,
                                                            landingPage: lp
                                                        })}
                                                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-slate-600 shadow-sm text-xs font-medium rounded text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-700 hover:bg-gray-50 dark:hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-blue mr-2"
                                                        title="Generează QR"
                                                    >
                                                        Generează QR
                                                    </button>
                                                    <button onClick={() => handleGenerateLink(lp.id)} className="text-blue-600 text-xs hover:underline mx-1">Link</button>
                                                    <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                                                        Aprobat
                                                    </span>
                                                </>
                                            ) : lp.status === 'respins' ? (
                                                <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">
                                                    Respins
                                                </span>
                                            ) : (
                                                <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800">
                                                    Pending
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        }
                    </div>

                    {/* My Active Links List */}
                    <div>
                        <h4 className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-3 uppercase tracking-wider">Link-uri Generate Active</h4>
                        <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
                            {myLinks.length === 0 ? <p className="p-6 text-sm text-gray-400 dark:text-gray-500">Niciun link generat.</p> :
                                <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
                                    <tbody className="bg-white dark:bg-transparent divide-y divide-gray-200 dark:divide-slate-700">
                                        {myLinks.map((link) => (
                                            <tr key={link.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors align-middle">
                                                <td className="px-5 py-5 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white align-middle">
                                                    <div className="flex items-center gap-3">
                                                        <span>{link.landingPage?.titlu}</span>
                                                        <span
                                                            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-600 dark:bg-slate-600/50 dark:text-slate-300"
                                                            title={`${link.leadsCount ?? 0} lead-uri de la acest link`}
                                                        >
                                                            <Users size={14} className="shrink-0" aria-hidden />
                                                            {link.leadsCount ?? 0} lead-uri
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-5 py-5 whitespace-nowrap text-right text-sm align-middle">
                                                    <div className="flex items-center justify-end gap-2">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleOpenQR(link)}
                                                            className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-700 hover:bg-gray-50 dark:hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-blue transition-colors"
                                                            title="Generează QR"
                                                            aria-label="Generează QR"
                                                        >
                                                            <QrCode size={18} aria-hidden />
                                                        </button>
                                                        {link.status === 'activ' ? (
                                                            <a
                                                                href={`${window.location.origin}/lp/${link.landingPage?.slug}?ref=${link.codUnic}`}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-700 hover:bg-gray-50 dark:hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-blue transition-colors"
                                                                title="Deschide Link"
                                                                aria-label="Deschide link în tab nou"
                                                            >
                                                                <ExternalLink size={18} aria-hidden />
                                                            </a>
                                                        ) : (
                                                            <span className="text-gray-400 dark:text-gray-500 text-xs">Indisponibil</span>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            }
                        </div>
                    </div>
                </div>
            </div>

            {showQRModal && (
                <QRModal
                    link={selectedQRLink}
                    onClose={() => { setShowQRModal(false); setSelectedQRLink(null); }}
                />
            )}
        </div>
    );
}




export default function AgentDashboard() {
    const location = useLocation();
    const isHome = location.pathname === '/agent' || location.pathname === '/agent/';
    const title = isHome ? 'Dashboard Agent' : (location.pathname.includes('leads') ? 'Lead-uri' : location.pathname.includes('ambassadors') ? 'Ambasadori' : location.pathname.includes('landing-pages') ? 'Landing Pages' : location.pathname.includes('editor-lp') ? 'Editor LP' : 'Profil');
    const subtitle = isHome ? 'Performanța ta și Lead-uri recente' : '';

    // Editor Landing Page V4 - full-screen (same as OP)
    if (location.pathname.match(/^\/agent\/editor-lp(\/\d+)?$/)) {
        return <LPEditorV4 />;
    }

    return (
        <PremiumLayout variant="agent" title={title} subtitle={subtitle}>
            <Routes>
                <Route path="/" element={<DashboardHome />} />
                <Route path="/leads" element={<MyLeads />} />
                <Route path="/ambassadors" element={<AgentAmbassadorsList />} />
                <Route path="/landing-pages" element={<LandingPages />} />
                <Route path="/profil" element={<AgentProfile />} />
            </Routes>
        </PremiumLayout>
    );
}
